import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import WizardProgress from '../site-setup-wizard/components/WizardProgress';
import { setupService } from '../../services/setupService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeError } from '../../utils/sanitizeError';

const ROLE_OPTIONS = [
  { value: 'site_manager', label: 'Site Manager' },
  { value: 'worker', label: 'Worker' }
];

const SetupFlowInviteTeam = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [teamMembers, setTeamMembers] = useState([
    { id: 1, email: '', name: '', role: 'worker' }
  ]);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const steps = [
    { id: 1, title: 'Company Details' },
    { id: 2, title: 'Choose Plan' },
    { id: 3, title: 'Create First Site' },
    { id: 4, title: 'Invite Team' },
    { id: 5, title: 'Daily Site Checklist' }
  ];

  useEffect(() => {
    loadSetupData();
  }, []);

  const loadSetupData = async () => {
    try {
      const { data, error } = await companyService?.getUserCompany();
      if (error) throw error;
      
      if (data) {
        setCompanyId(data?.id);
      }

      const savedProgress = setupService?.loadProgress();
      if (savedProgress?.formData?.siteId) {
        setSiteId(savedProgress?.formData?.siteId);
      }
      if (savedProgress?.formData?.siteName) {
        setSiteName(savedProgress?.formData?.siteName);
      }
      if (savedProgress?.formData?.teamMembers) {
        setTeamMembers(savedProgress?.formData?.teamMembers);
      }
    } catch (err) {
      console.error('Failed to load setup data:', err);
    } finally {
      setLoading(false);
    }
  };

  const addMember = () => {
    setTeamMembers(prev => [
      ...prev,
      { id: Date.now(), email: '', name: '', role: 'worker' }
    ]);
  };

  const removeMember = (id) => {
    setTeamMembers(prev => prev?.filter(m => m?.id !== id));
  };

  const updateMember = (id, field, value) => {
    setTeamMembers(prev => prev?.map(m => m?.id === id ? { ...m, [field]: value } : m));
    setError('');
  };

  const handleBack = () => {
    navigate('/setup-flow-create-site');
  };

  const handleSkip = () => {
    setupService?.saveProgress(4, { teamMembers: [], siteId, siteName });
    navigate('/setup-flow-checklist-confirmation');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const filledMembers = teamMembers?.filter(m => m?.email?.trim());

    if (filledMembers?.length === 0) {
      // Allow skipping
      handleSkip();
      return;
    }

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = filledMembers?.filter(m => !emailRegex?.test(m?.email?.trim()));
    if (invalidEmails?.length > 0) {
      setError(`Invalid email: ${invalidEmails?.[0]?.email}`);
      return;
    }

    // Note: siteId is optional here — workers/site_managers can be invited without a site
    // The inviteTeamMembersPerRole method handles role-based site assignment

    setIsSubmitting(true);
    try {
      // Map members to include siteId only if available (optional for onboarding)
      const membersWithSite = filledMembers?.map(m => ({
        ...m,
        siteId: siteId || ''
      }));

      const { data: inviteResults, error: inviteError } = await setupService?.inviteTeamMembersPerRole(
        companyId,
        membersWithSite
      );

      if (inviteError) throw inviteError;

      setResults(inviteResults || []);
      setupService?.saveProgress(4, { teamMembers: filledMembers, siteId, siteName });

      // Navigate to next step after short delay
      setTimeout(() => {
        navigate('/setup-flow-checklist-confirmation');
      }, 1200);
    } catch (err) {
      setError(sanitizeError(err?.message, 'Failed to invite team members'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Invite Your Team</h1>
          <p className="text-muted-foreground">Add team members to your company — you can always add more later</p>
        </div>

        <WizardProgress currentStep={4} totalSteps={5} steps={steps} onExit={() => navigate('/')} />

        <div className="bg-card rounded-lg shadow-elevation-1 p-6 md:p-8">
          {siteName && (
            <div className="bg-accent/10 border border-accent/20 rounded-md p-4 mb-6 flex items-center gap-3">
              <Icon name="Building2" size={18} className="text-accent flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">First site: {siteName}</p>
                <p className="text-xs text-muted-foreground">Site managers and workers can be assigned to this site after setup</p>
              </div>
            </div>
          )}

          <h2 className="text-xl font-semibold text-foreground mb-2">Team Members</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Add site managers and workers. Site assignments can be made after setup is complete.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {teamMembers?.map((member, index) => (
              <div key={member?.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">Team Member {index + 1}</span>
                  {teamMembers?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMember(member?.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Email address"
                    type="email"
                    value={member?.email}
                    onChange={(e) => updateMember(member?.id, 'email', e?.target?.value)}
                    placeholder="worker@company.com"
                  />
                  <Input
                    label="Full name (optional)"
                    value={member?.name}
                    onChange={(e) => updateMember(member?.id, 'name', e?.target?.value)}
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
                  <div className="flex gap-3">
                    {ROLE_OPTIONS?.map(opt => (
                      <button
                        key={opt?.value}
                        type="button"
                        onClick={() => updateMember(member?.id, 'role', opt?.value)}
                        className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-all ${
                          member?.role === opt?.value
                            ? 'border-accent bg-accent/10 text-accent' :'border-border text-muted-foreground hover:border-accent/50'
                        }`}
                      >
                        {opt?.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addMember}
              className="w-full py-3 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2"
            >
              <Icon name="Plus" size={16} />
              Add another team member
            </button>

            {results?.length > 0 && (
              <div className="space-y-2">
                {results?.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 text-sm p-2 rounded-md ${
                      r?.success ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
                    }`}
                  >
                    <Icon name={r?.success ? 'CheckCircle' : 'AlertCircle'} size={14} />
                    <span>{r?.email} — {r?.success ? 'Invite sent' : r?.error}</span>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex justify-between items-center pt-4">
              <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={isSubmitting}
                >
                  Skip for now
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  size="lg"
                >
                  Continue
                </Button>
              </div>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Step 4 of 5 — You can invite more team members from the Users page later
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupFlowInviteTeam;
